// Contextual messages displayed on the upgrade page based on the reason for upgrade
export const CONTEXTUAL_MESSAGES: Record<
  string,
  { title: string; subtitle: string }
> = {
  no_credits: {
    title: "You've used all your credits",
    subtitle:
      "Use a one-time top-up for a busy week, or upgrade if you need more room every month.",
  },
  created_banner: {
    title: "Keep creating",
    subtitle:
      "Upgrade for more credits, no watermark, and autopilot scheduling.",
  },
  watermark: {
    title: "Remove the watermark",
    subtitle:
      "Upgrade to publish clean, professional posts without the WaveGen watermark.",
  },
  export_gate: {
    title: "Download your posts",
    subtitle:
      "Upgrade to download watermark-free files in full quality. Prefer not to? Publishing to your connected channels is always free.",
  },
  connection_limit: {
    title: "Connect more accounts",
    subtitle: "Upgrade to manage all your social media accounts in one place.",
  },
  first_comment: {
    title: "Unlock First Comment",
    subtitle:
      "Boost engagement by automatically adding a first comment to your posts.",
  },
  autopilot_activate: {
    title: "Unlock Autopilot",
    subtitle: "Let WaveGen automatically create and schedule posts for you.",
  },
  autopilot_add_source: {
    title: "Add more content sources",
    subtitle:
      "Upgrade to build libraries from multiple sources and get more content ideas on autopilot.",
  },
  "brandkit-limit": {
    title: "Create more brand kits",
    subtitle: "Upgrade to save more brand kits and keep your content on-brand.",
  },
  batch_create_insufficient_credits: {
    title: "Not enough credits",
    subtitle:
      "Top up for this batch, or upgrade if this volume is becoming normal.",
  },
  sidebar_cta: {
    title: "Upgrade your plan",
    subtitle: "Get more generated posts, connections, and premium features.",
  },
  mobile_header: {
    title: "Upgrade your plan",
    subtitle: "Get more generated posts, connections, and premium features.",
  },
};

export const DEFAULT_MESSAGE = {
  title: "Upgrade your plan",
  subtitle: "Get more generated posts, connections, and premium features.",
};

// Business plan features (always shown as the last card)
export const BUSINESS_FEATURES = [
  "All features in Pro plan",
  "Custom post generation credits",
  "Unlimited social media connections",
  "Unlimited brand kits",
  "Bespoke branded templates",
  "Done-for-you social media service",
  "Priority customer support",
];

export const BUSINESS_CONTACT_URL =
  "https://automatic-knight-27d.notion.site/2df6114c3d7780dab1eef88de0b8b421?pvs=105";

// Social proof avatars (used in the "Trusted by" strip)
export const SOCIAL_PROOF_AVATARS = [
  { src: "/images/marjai.jpg", alt: "Marjai" },
  { src: "/images/tim.jpeg", alt: "Tim" },
  { src: "/images/pio.jpg", alt: "Pio" },
  { src: "/images/bree.jpeg", alt: "Bree" },
  { src: "/images/deniz.jpg", alt: "Deniz" },
];

// Testimonials for the upgrade page (extracted from website)
export interface UpgradeTestimonial {
  quote: string;
  name: string;
  role: string;
  avatar: string;
}

export const TESTIMONIALS: UpgradeTestimonial[] = [
  {
    quote:
      "WaveGen has been a huge time save for my content creation! Now I get multiple ready to post videos when I finish a new blog post. The process is very clear, UX is excellent, and I really like it!",
    name: "Tim Rohling",
    role: "Consultant",
    avatar: "/images/tim.jpeg",
  },
  {
    quote:
      "Honestly, WaveGen has been a game changer since day one. I've tried many tools and automations to help with social media content, and nothing comes close.",
    name: "Pio Scelina",
    role: "Growth Marketer",
    avatar: "/images/pio.jpg",
  },
  {
    quote: "Three of my top 5 LinkedIn posts are now WaveGen carousels!",
    name: "Bree Sharp",
    role: "Content Writer",
    avatar: "/images/bree.jpeg",
  },
  {
    quote:
      "This is a game changer for me! Now I feel empowered to share my business more broadly across multiple platforms without getting overwhelmed.",
    name: "Marjai Roberts",
    role: "Realtor",
    avatar: "/images/marjai.jpg",
  },
];

// FAQ items relevant to upgrade/pricing
export interface UpgradeFaqItem {
  question: string;
  answer: string;
}

export const FAQ_ITEMS: UpgradeFaqItem[] = [
  {
    question: "Is there a free trial?",
    answer:
      "Yes! You get 10 free posts and one social media connection to try out the platform. No credit card required!",
  },
  {
    question: "Can I cancel my subscription anytime?",
    answer:
      "Yes, you can cancel your subscription anytime in your billing portal. The cancellation will be effective at the end of the current billing period, and you can continue to use WaveGen until then.",
  },
  {
    question: "Can I get a refund if I'm not satisfied?",
    answer:
      "Yes, on a paid plan, you're eligible for a full refund within 7 days if you've generated 5 or fewer posts. Beyond that we're unable to refund, because every post runs real generative-AI compute (image generation and language models) that our providers bill us for and don't refund. You can cancel anytime — your plan won't renew and you won't be charged again.",
  },
  {
    question: "How many posts can I publish/schedule in a month?",
    answer:
      "As many as you want! We don't limit the number of posts you can publish or schedule. Even though the posts you can generate are limited by your plan, you can use WaveGen as a normal scheduler -- publish and schedule your content (WaveGen generated or not) as many times as you want.",
  },
  {
    question: "Will my posts sound like me, or like generic AI?",
    answer:
      "WaveGen starts from your own writing -- not random AI text -- so your voice and ideas come through in every post. Plus, you get full control to edit and fine-tune everything using an intuitive editor before publishing.",
  },
  {
    question: "What social platforms does WaveGen support?",
    answer:
      "We currently support YouTube, Instagram (both feed and stories), TikTok, Facebook, and LinkedIn. We're actively working on integrating more platforms.",
  },
];
